import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Navbar from '../components/brand/Navbar';
import Footer from '../components/brand/Footer';
import { blogPosts } from '../data/blogPosts';

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Brand-navy placeholder rendered beneath every card image.
 * Visible when the heroImage file hasn't been added yet (img onError hides itself)
 * or when post.heroImage is absent.
 */
const CardImagePlaceholder = () => (
  <div
    aria-hidden="true"
    style={{
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(135deg, #2B3879 0%, #1a245e 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <svg
      width="48"
      height="48"
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="6" y="12" width="36" height="26" rx="3"
        stroke="rgba(255,255,255,0.22)" strokeWidth="1.5"
      />
      <circle
        cx="18" cy="23" r="5"
        stroke="rgba(255,255,255,0.22)" strokeWidth="1.5"
      />
      <path
        d="M6 31l10-8 7 5 9-10 10 11"
        stroke="rgba(255,255,255,0.22)" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  </div>
);

const Blog = () => {
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'view_blog_listing', {
        event_category: 'engagement',
        event_label: 'Blog listing page viewed',
      });
    }
  }, []);

  const sorted = [...blogPosts].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <>
      <Helmet>
        <title>Blog | Mobile Pathology Insights | Nura Health</title>
        <meta
          name="description"
          content="Expert insights on mobile blood collection, at-home pathology, aged care pathology services, and NDIS pathology collection across Brisbane, Sydney and Melbourne metros."
        />
        <link rel="canonical" href="https://nurahealth.com.au/blog" />
      </Helmet>

      <div className="min-h-screen" style={{ background: '#F7F9FB' }}>
        <Navbar onBookClick={() => { window.location.href = '/booking'; }} />

        <main>
          {/* ── Editorial page header ────────────────────────────────────── */}
          <section style={{ paddingTop: '8.5rem', paddingBottom: '2.75rem', background: '#F7F9FB' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem', textAlign: 'center' }}>

              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" style={{ marginBottom: '1.4rem' }}>
                <Link
                  to="/"
                  style={{
                    color: '#2B3879',
                    opacity: 0.5,
                    fontSize: '0.78rem',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    fontWeight: 500,
                    textDecoration: 'none',
                    letterSpacing: '0.01em',
                  }}
                >
                  Home
                </Link>
                <span
                  aria-hidden="true"
                  style={{
                    color: '#2B3879',
                    opacity: 0.3,
                    margin: '0 0.5rem',
                    fontSize: '0.78rem',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  }}
                >
                  —
                </span>
                <span
                  aria-current="page"
                  style={{
                    color: '#2B3879',
                    opacity: 0.55,
                    fontSize: '0.78rem',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  Blog
                </span>
              </nav>

              {/* H1 */}
              <h1
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontSize: 'clamp(2.2rem, 5vw, 3.25rem)',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.08,
                  color: '#2B3879',
                  marginBottom: '0.85rem',
                }}
              >
                Nura Health Blog
              </h1>

              {/* Subhead */}
              <p
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  color: '#2B3879',
                  opacity: 0.65,
                  fontSize: '1.05rem',
                  lineHeight: 1.65,
                  maxWidth: '560px',
                  margin: '0 auto',
                }}
              >
                Insights on mobile pathology, at-home blood collection, and healthcare
                convenience across Brisbane, Sydney and Melbourne metros.
              </p>
            </div>
          </section>

          {/* Thin rule */}
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '0 1.5rem',
              borderTop: '1px solid rgba(43,56,121,0.09)',
            }}
          />

          {/* ── Card grid ────────────────────────────────────────────────── */}
          <section style={{ padding: '3rem 0 5.5rem' }}>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}
            >
              {sorted.map((post, idx) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group"
                  style={{ textDecoration: 'none', display: 'block' }}
                >
                  <article
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      background: '#ffffff',
                      border: '1px solid rgba(43,56,121,0.10)',
                      boxShadow: '0 2px 16px rgba(43,56,121,0.06)',
                      transition: 'transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 14px 44px rgba(43,56,121,0.13)';
                      e.currentTarget.style.borderColor = 'rgba(43,56,121,0.20)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 16px rgba(43,56,121,0.06)';
                      e.currentTarget.style.borderColor = 'rgba(43,56,121,0.10)';
                    }}
                  >
                    {/* ── Hero image ── */}
                    <div
                      style={{
                        position: 'relative',
                        aspectRatio: '3 / 2',
                        overflow: 'hidden',
                        flexShrink: 0,
                      }}
                    >
                      {/* Placeholder — always present; img layers on top */}
                      <CardImagePlaceholder />

                      {post.heroImage && (
                        <img
                          src={post.heroImage}
                          alt={post.title}
                          width={600}
                          height={400}
                          loading={idx === 0 ? 'eager' : 'lazy'}
                          className="group-hover:scale-105"
                          style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            transition: 'transform 0.5s ease',
                            transformOrigin: 'center center',
                          }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                    </div>

                    {/* ── Card body ── */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        padding: '1.4rem 1.6rem 1.6rem',
                      }}
                    >
                      {/* Meta row */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          marginBottom: '0.65rem',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            color: '#2B3879',
                            fontSize: '0.72rem',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                            fontWeight: 500,
                            opacity: 0.7,
                          }}
                        >
                          {formatDate(post.date)}
                        </span>
                        <span style={{ color: '#2B3879', opacity: 0.28, fontSize: '0.72rem' }}>·</span>
                        <span
                          style={{
                            color: '#2B3879',
                            fontSize: '0.72rem',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                            opacity: 0.6,
                          }}
                        >
                          {post.readTime}
                        </span>
                      </div>

                      {/* Title */}
                      <h2
                        style={{
                          color: '#00001E',
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontSize: '1.08rem',
                          fontWeight: 600,
                          lineHeight: 1.42,
                          marginBottom: '0.55rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {post.title}
                      </h2>

                      {/* Excerpt */}
                      <p
                        style={{
                          color: '#2B3879',
                          opacity: 0.76,
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          fontSize: '0.88rem',
                          lineHeight: 1.62,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          flex: 1,
                          marginBottom: '1.2rem',
                        }}
                      >
                        {post.excerpt}
                      </p>

                      {/* Footer CTA */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.4rem',
                          color: '#2B3879',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                        }}
                      >
                        Read article
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M3 8H13M13 8L9 4M13 8L9 12"
                            stroke="#2B3879"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
